/**
 * Pending admissions (1.3): what has been accepted and reserved and has not
 * started, as one table sorted by expected discharge. Three actions on every row
 * — Release, Update, Welcome call — and nothing else. Release confirms first:
 * the slot it gives back is not something to hand over by a stray click. A hold
 * whose day passed is flagged, never silently released.
 *
 * A row leaves when the welcome call converts its hold or when it is released.
 * There is no converted group; the proof a call worked is the visit on the
 * clinician's day.
 */
import { useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useNow } from "../../components/useNow";
import { WelcomeCallWizard } from "../../components/WelcomeCallWizard";
import { addDaysToKey, dateKeyStart, formatInBranchZone } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { selectPendingList, type PendingRow } from "../../engine/pendingList";
import { reHold, releaseHold } from "../../presenter/actions";
import { useDemoState } from "../../store/useDemoStore";
import { useBranchClock } from "../../components/branchClock";

const PAYER_LABEL = { MA: "Medicare Advantage", FFS: "Medicare FFS" } as const;

export function PendingList() {
  const state = useDemoState();
  const now = useNow();
  const rows = useMemo(() => selectPendingList(state, now), [state, now]);
  const [wizardFor, setWizardFor] = useState<string | undefined>();
  const [reHoldFor, setReHoldFor] = useState<PendingRow | undefined>();
  const [releaseFor, setReleaseFor] = useState<PendingRow | undefined>();
  const tz = state.config.branch.timeZone;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper variant="outlined">
        <Table size="small" sx={{ "& td, & th": { py: 0.75 } }}>
          <TableHead>
            <TableRow>
              <TableCell>Patient</TableCell>
              <TableCell>Expected plan</TableCell>
              <TableCell>Expected discharge</TableCell>
              <TableCell>Start-of-care hold</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    No pending admissions.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <Row key={row.holdId} row={row} tz={tz} onWelcomeCall={() => setWizardFor(row.patientRef)} onReHold={() => setReHoldFor(row)} onRelease={() => setReleaseFor(row)} />
            ))}
          </TableBody>
        </Table>
      </Paper>

      {wizardFor && <WelcomeCallWizard open onClose={() => setWizardFor(undefined)} patientRef={wizardFor} mode="welcomeCall" />}
      {reHoldFor && <ReHoldDialog row={reHoldFor} onClose={() => setReHoldFor(undefined)} />}
      {releaseFor && <ReleaseDialog row={releaseFor} onClose={() => setReleaseFor(undefined)} />}
    </Box>
  );
}

function Row({ row, tz, onWelcomeCall, onReHold, onRelease }: { row: PendingRow; tz: string; onWelcomeCall: () => void; onReHold: () => void; onRelease: () => void }) {
  const clock = useBranchClock(tz);
  const expired = row.state === "expired";
  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" fontWeight={600}>
            {row.patientName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {PAYER_LABEL[row.payer]}
          </Typography>
          {row.state === "expired" && <Chip size="small" color="error" variant="outlined" label="Hold expired" />}
          {row.dischargeChanged && <Chip size="small" color="warning" label="Discharge date changed" />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {row.source}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.expectedPlanLabel}</Typography>
        <Typography variant="caption" color="text.secondary">
          {row.tail.map((t) => `~${t.pointsPerWeek} points/wk ${t.discipline}${t.requiredTraits.length ? ` (${t.requiredTraits.join(", ")})` : ""}`).join(" · ") || "nothing reserved"}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{formatInBranchZone(dateKeyStart(row.expectedDischarge, tz), tz, "EEE MMM d")}</Typography>
        <Typography variant="caption" sx={{ color: row.socByToday ? "error.main" : "text.secondary", fontWeight: row.socByToday ? 600 : undefined }}>
          SOC by {formatInBranchZone(new Date(row.socDueByIso), tz, "EEE MMM d")}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {/* **Needs a time**, not a blank and not "unassigned" (script author,
              discussion turn 48). A hold whose day could not hold it gives up
              its hour and its clinician, and a blank column reads on stage as a
              rendering fault where the words read as the model. The worklist
              keeps the longer sentence; these two words are in both.

              An **expired** hold says so instead, and says nothing else: the
              Hold expired chip and the red SOC-by carry the rest, and reading
              "Needs a time" against a date a week gone is the column claiming a
              live hold (script author, discussion turn 68). */}
          {expired ? "Expired" : row.clinicianName ?? "Needs a time"} · {formatInBranchZone(dateKeyStart(row.date, tz), tz, "EEE MMM d")}
        </Typography>
        {/* **An arrival window only where there is one to promise.** A hold with
            no clinician has no hour, and the window under a Needs a time row was
            the fixture's old position leaking through — it promised the patient
            a window the branch has not got. Nothing replaces it: Expected
            discharge already carries SOC by. */}
        {!expired && row.clinicianName && (
          <Typography variant="caption" color="text.secondary">
            arrival {clock.wall(row.windowStart)}–{clock.wall(row.windowEnd)}
          </Typography>
        )}
        {!expired && row.awaitingAuth && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            welcome call done · awaiting SOC auth
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
          <Button size="small" variant="text" onClick={onRelease}>
            Release
          </Button>
          <Button size="small" variant={row.dischargeChanged ? "contained" : "outlined"} color={row.dischargeChanged ? "warning" : "primary"} onClick={onReHold}>
            Update
          </Button>
          <Button size="small" variant={row.welcomeCallOpen ? "contained" : "outlined"} onClick={onWelcomeCall}>
            Welcome call
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ReleaseDialog({ row, onClose }: { row: PendingRow; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Release hold for {row.patientName}?</DialogTitle>
      <DialogActions>
        <Button onClick={onClose}>Keep hold</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={() => {
            releaseHold(row.holdId);
            onClose();
          }}
        >
          Release
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReHoldDialog({ row, onClose }: { row: PendingRow; onClose: () => void }) {
  const [date, setDate] = useState<string>(row.dischargeChanged ? row.expectedDischarge : addDaysToKey(row.expectedDischarge, 1));
  const [error, setError] = useState<string | undefined>();
  const confirm = () => {
    const slot = reHold(row.holdId, date);
    if (!slot) {
      setError(`No start-of-care slot inside 48 hours of ${date}.`);
      return;
    }
    onClose();
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Update Pending Admission · {row.patientName}</DialogTitle>
      <DialogContent>
        <TextField label="Expected discharge" type="date" size="small" fullWidth value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ mt: 1 }} />
        {error && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={confirm}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
}
